/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 03-Aug-2026
 * @author      : Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Automation Plan Engine A2 — build-spec §7 (D45) — Provider Web, master catalog
 * Purpose      : The provider-admin authoring list for automation plan masters — every plan, every
 *                version, every status — with New / Edit steps / Publish / Retire / New version.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * A2 §7          | 03-Aug-2026 | Aravindan      | Initial creation
 * A2 §7          | 04-Aug-2026 | Aravindan      | Delete action (catalog-only) completes CRUD
 * PR 7871 #66454 | 11-Aug-2026 | Claude         | Publish/New-version failures render INSIDE the
 *                                                 confirm dialog, and a 400/409 reloads the grid.
 * PR 7871 #66482 | 11-Aug-2026 | Claude         | Removed the unreachable retire/delete pending
 *                                                 variants, handler branches and dialog copy left
 *                                                 behind when those buttons were dropped.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import HtmlPreview from '../communications/HtmlPreview';
import { fetchManageCatalog } from '../communications/MessageTemplateService';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import { AsyncBoundary, useAsyncResource } from '../communications/AsyncStates';
import {
  deleteMaster,
  fetchPlanMasters,
  fetchPlanMasterSteps,
  fetchPlanMasterVersions,
  openNewVersion,
  publishVersion,
} from './AutomationMasterService';
import {
  PlanMasterStep,
  PlanMasterSummaryDTO,
  PlanStepReadDTO,
  actionLabel,
  canOpenNewVersion,
  fromStepDTOs,
  isPublishable,
  statusColor,
  statusLabel,
  timingLabel,
} from './AutomationTypes';
import { PlanMasterBuilder, PlanMasterBuilderTarget } from './PlanMasterBuilder';

/**
 * ONE ROW IS ONE (planMasterUid, VERSION) — NOT ONE PLAN.
 *
 * `automation_plan_master` versions a plan by row, and `PlanMasterSummaryDTO` carries the version
 * as a first-class field. Every action on this screen is version-scoped: `publish` and `retire`
 * both REQUIRE an explicit version (the service has no "current draft" resolver), and steps are
 * replaced on one `(uid, version)` pair. Collapsing the grid to one row per plan would mean
 * guessing which version an action applied to — the exact class of mistake B11 catalogues on the
 * template review queue.
 *
 * The provider-api list has no server ordering (`findAll()`, unsorted), so sorting here is safe.
 * Name then version descending puts the newest version of each plan at the top of its group.
 */

interface PlanRow extends PlanMasterSummaryDTO {
  /** DataGrid needs a unique id, and neither uid nor version is unique on its own. */
  id: string;
}

/**
 * Vertical space (px) taken up above the grid, subtracted from 100vh so the grid fills the
 * remaining viewport instead of a hard-coded height that left a dead gap on tall screens.
 * Breakdown: ~64px fixed topbar + ~136px this page's header/padding.
 */
const GRID_TOP_OFFSET_PX = 200; // 64 topbar + ~136 header/padding

const toRows = (masters: PlanMasterSummaryDTO[]): PlanRow[] =>
  [...masters]
    // 2026-08-11, at the user's request: show only DRAFT and PUBLISHED. SUPERSEDED (an older
    // version replaced when a newer one was published) and RETIRED are backend lifecycle states,
    // not authoring states — hiding their rows keeps the list to the two states an author acts on.
    // This is a display filter only; PlanMasterStatus and the backend enum are unchanged, and one
    // row is still one (uid, version) so every action stays version-scoped.
    .filter((m) => m.status === 'DRAFT' || m.status === 'PUBLISHED')
    .map((m) => ({ ...m, id: `${m.planMasterUid}::${m.version}` }))
    .sort((a, b) => {
      const byName = (a.name ?? '').localeCompare(b.name ?? '');
      return byName !== 0 ? byName : b.version - a.version;
    });

/** Case-insensitive match on name or description — the two free-text fields a row shows. */
const matchesSearch = (row: PlanRow, search: string): boolean => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    (row.name ?? '').toLowerCase().includes(needle) ||
    (row.description ?? '').toLowerCase().includes(needle)
  );
};

/**
 * Only the two actions this screen actually offers.
 *
 * Retire and Delete were removed from the provider catalog UI per request — a published master is
 * managed via Publish / New version only. Their `PendingAction` variants, `runPending` branches and
 * confirm-dialog copy went with the buttons: nothing could set `pending` to either kind, so they
 * were unreachable code that read as live capability (down to a confirm dialog no user could open).
 * `retireVersion` / `deleteMaster` are still exported by `AutomationMasterService` — the endpoints
 * exist and are documented there — so restoring the buttons is a UI change only.
 */
/** One labelled read-only field, styled like the step editor's ACTION TYPE / STEP NAME captions. */
const PreviewField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' }}
    >
      {label}
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

/**
 * The read-only field set for one step — mirrors what the step editor shows for that kind (Action
 * type, Step name, Timing, then the per-kind fields: Template/Subject for email, Template/Message
 * for SMS, and so on). Email's rendered body is shown separately as a LIVE PREVIEW below.
 */
/**
 * A template reference resolved to something a human can read. The plan step stores only a
 * `templateMasterUid`, so without this the preview shows a bare UUID — see `resolveTemplate`.
 */
export interface ResolvedTemplate {
  name: string;
  subject: string | null;
  body: string;
}

const stepFields = (
  step: PlanMasterStep,
  resolve?: (uid: string) => ResolvedTemplate | undefined,
): Array<{ label: string; value: string }> => {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Action type', value: actionLabel(step.kind) },
    { label: 'Step name', value: step.label?.trim() || '—' },
    { label: 'Timing', value: timingLabel(step) },
  ];
  switch (step.kind) {
    case 'email': {
      const tpl = step.template ? resolve?.(step.template) : undefined;
      rows.push({ label: 'Template', value: tpl?.name || step.template || '—' });
      rows.push({ label: 'Subject', value: step.subject || tpl?.subject || '—' });
      break;
    }
    case 'sms': {
      const tpl = step.smsTemplate ? resolve?.(step.smsTemplate) : undefined;
      rows.push({ label: 'Template', value: tpl?.name || step.smsTemplate || '—' });
      rows.push({ label: 'Message', value: step.message || tpl?.body || '—' });
      break;
    }
    case 'moveplan':
      rows.push({ label: 'Move to plan', value: step.targetPlan || '—' });
      break;
    case 'stage':
      rows.push(
        step.stageAction === 'plan'
          ? { label: 'Move to plan', value: step.targetPlan || '—' }
          : { label: 'Update stage to', value: step.targetStage || '—' },
      );
      break;
    case 'notify':
      rows.push({ label: 'Title', value: step.notifyTitle || '—' });
      rows.push({ label: 'Body', value: step.notifyBody || '—' });
      break;
    case 'wait':
      rows.push({
        label: 'Wait',
        value: step.waitMode === 'days' ? `${step.waitDays || '0'} day(s)` : (step.waitMode || '—'),
      });
      break;
    default:
      break;
  }
  return rows;
};

/**
 * Read-only step viewer (2026-08-11, at the user's request — the "preview" the plan list lacked,
 * now laid out like the step editor screen rather than a bare list).
 *
 * A PUBLISHED plan's "Edit steps" is disabled (its steps are immutable once forkable), so there was
 * previously NO way to see what a published plan actually does from this list. Each step renders as
 * a read-only card mirroring the editor's field layout — Action type, Step name, Timing, the
 * per-kind fields, and (for email) a LIVE PREVIEW of the rendered body — but with no drag, no
 * inputs, and no save. Deliberately NOT the full `PlanMasterBuilder`, which is an authoring surface.
 */
const PlanStepsPreviewDialog: React.FC<{ target: PlanRow; onClose: () => void }> = ({
  target,
  onClose,
}) => {
  const { accessToken } = useOidcAccessToken();
  const { state, reload } = useAsyncResource<PlanStepReadDTO[]>(
    () => fetchPlanMasterSteps(target.planMasterUid, target.version, accessToken),
    [target.planMasterUid, target.version, accessToken],
  );
  // A step stores only a templateMasterUid, so on its own this dialog could show nothing but a
  // bare UUID and — because the Live preview below was gated on an INLINE body that
  // template-backed steps never carry — no preview at all. Load the catalog once per open and
  // resolve each reference to its name/subject/body. Failure is non-fatal: the map stays empty
  // and every field falls back to the uid, exactly as before.
  const [templates, setTemplates] = useState<Map<string, ResolvedTemplate>>(new Map());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchManageCatalog(accessToken);
      if (cancelled || result.kind !== 'ok') return;
      // Newest version wins, so a revised template previews as what tenants actually receive.
      const seen = new Map<string, number>();
      const resolved = new Map<string, ResolvedTemplate>();
      result.data.forEach((t) => {
        const prev = seen.get(t.templateMasterUid);
        if (prev !== undefined && prev >= t.version) return;
        seen.set(t.templateMasterUid, t.version);
        resolved.set(t.templateMasterUid, { name: t.name, subject: t.subject, body: t.body });
      });
      setTemplates(resolved);
    })();
    return () => { cancelled = true; };
  }, [accessToken]);
  const resolveTemplate = useCallback(
    (uid: string): ResolvedTemplate | undefined => templates.get(uid),
    [templates],
  );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {(target.name ?? 'Plan')} · v{target.version} — steps (read-only)
      </DialogTitle>
      <DialogContent dividers>
        {/* Plan-level settings — so a reviewer sees WHAT the shared plan is (trigger/lead source/
            chain/origin) before publishing, not only its steps. entryStage + goal = "Trigger". */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Plan settings</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <PreviewField label="Lead source" value={target.leadSource || 'Any'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PreviewField
                label="Trigger"
                value={
                  target.entryStage
                    ? `Stage: ${target.entryStage}${target.goalKind ? ` · goal ${target.goalKind}${target.goalParam ? `=${target.goalParam}` : ''}` : ''}`
                    : (target.goalKind ? `Goal ${target.goalKind}${target.goalParam ? `=${target.goalParam}` : ''}` : 'No trigger')
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PreviewField label="Next plan" value={target.nextPlanMasterUid ? 'Chains to another plan' : 'None'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PreviewField label="Origin" value={target.contributedByTenant ? `Contributed by ${target.contributedByTenant}` : 'Provider-authored'} />
            </Grid>
          </Grid>
        </Paper>
        <AsyncBoundary
          state={state}
          what="This plan version's steps"
          onRetry={reload}
          loadingLabel="Loading steps…"
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No steps on this version"
          emptyDetail="This plan version has no steps — a tenant forking it would get an empty plan."
        >
          {(rows) => (
            <Stack spacing={2}>
              {fromStepDTOs(rows).map((step, index) => (
                <Paper key={step.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Step {index + 1}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {stepFields(step, resolveTemplate).map((field) => (
                      <Grid item xs={12} sm={6} key={field.label}>
                        <PreviewField label={field.label} value={field.value} />
                      </Grid>
                    ))}
                  </Grid>
                  {(() => {
                    // Prefer an inline body, else the referenced template's. Template-backed
                    // steps are the norm for provider plans, and gating on step.body alone meant
                    // the preview never rendered for any of them.
                    if (step.kind !== 'email') return null;
                    const html = step.body || (step.template ? resolveTemplate(step.template)?.body : undefined);
                    if (!html) return null;
                    return (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.5 }}
                        >
                          Live preview
                        </Typography>
                        <HtmlPreview html={html} testId={`preview-body-${step.id}`} minHeight={160} />
                      </Box>
                    );
                  })()}
                </Paper>
              ))}
            </Stack>
          )}
        </AsyncBoundary>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Read-only version-history dialog (Comms gap-fix #2, 2026-08-13) — groups one plan family's whole
 * lineage instead of the flat (uid, version) rows the grid shows. Fetches `GET …/masters/{uid}/
 * versions` (newest first) and lists each version's status/lead-source/trigger/description. The
 * grid stays flat on purpose (every action there is version-scoped); this is the "see the lineage"
 * companion, opened from the row's history icon — same open/close shape as PlanStepsPreviewDialog.
 */
const PlanVersionHistoryDialog: React.FC<{ target: PlanRow; onClose: () => void }> = ({
  target,
  onClose,
}) => {
  const { accessToken } = useOidcAccessToken();
  const { state, reload } = useAsyncResource<PlanMasterSummaryDTO[]>(
    () => fetchPlanMasterVersions(target.planMasterUid, accessToken),
    [target.planMasterUid, accessToken],
  );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{(target.name ?? 'Plan')} — version history</DialogTitle>
      <DialogContent dividers>
        <AsyncBoundary
          state={state}
          what="This plan's version history"
          onRetry={reload}
          loadingLabel="Loading version history…"
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No versions found"
          emptyDetail="This plan family has no non-deleted versions."
        >
          {(rows) => (
            <Stack spacing={1.5}>
              {rows.map((v) => (
                <Paper key={`${v.planMasterUid}::${v.version}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Version {v.version}</Typography>
                    <Chip size="small" label={statusLabel(v.status)} color={statusColor(v.status)} />
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <PreviewField label="Lead source" value={v.leadSource || 'Any'} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PreviewField
                        label="Trigger"
                        value={v.entryStage ? `Stage: ${v.entryStage}` : 'No trigger'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PreviewField label="Next plan" value={v.nextPlanMasterUid ? 'Chains to another plan' : 'None'} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PreviewField label="Origin" value={v.contributedByTenant ? `Contributed by ${v.contributedByTenant}` : 'Provider-authored'} />
                    </Grid>
                    <Grid item xs={12}>
                      <PreviewField label="Description" value={v.description || '—'} />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          )}
        </AsyncBoundary>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

type PendingAction =
  | { kind: 'publish'; row: PlanRow }
  | { kind: 'newVersion'; row: PlanRow }
  // Delete is offered ONLY for a tenant-contributed draft (see the actions renderCell): a plan a
  // tenant shared is Publish-or-Delete on the provider side — never edited here.
  | { kind: 'delete'; row: PlanRow };

export const AutomationPlansPage: React.FC = () => {
  const { accessToken } = useOidcAccessToken();

  const { state, reload } = useAsyncResource<PlanMasterSummaryDTO[]>(
    () => fetchPlanMasters(accessToken),
    [accessToken],
  );

  /** The plan-master builder modal — open when a (uid, version) draft is being authored. */
  const [builderTarget, setBuilderTarget] = useState<PlanMasterBuilderTarget | null>(null);
  /** The read-only step preview dialog — open when viewing (not editing) a version's steps. */
  const [previewTarget, setPreviewTarget] = useState<PlanRow | null>(null);
  /** The read-only version-history dialog (gap-fix #2) — one plan family's whole lineage. */
  const [historyTarget, setHistoryTarget] = useState<PlanRow | null>(null);
  /** Free-text filter (name/description) for the grid — see `matchesSearch`. */
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // The builder reports a partial outcome (created but steps failed) with severity 'warning' so it
  // is not shown in the green success channel — see PlanMasterBuilder onSaved (review finding #4).
  const [noticeSeverity, setNoticeSeverity] = useState<'success' | 'warning'>('success');

  /**
   * The publish / new-version dialog's OWN failure, for exactly the reason `createFailure` exists.
   *
   * The page-level banner this screen used to carry rendered above the grid, which the still-open
   * MUI Dialog covers, and the dialog is only closed on the SUCCESS path — so a rejected Publish (the version is no longer DRAFT: 400/409)
   * used to show the admin nothing at all: the dialog stayed open, Confirm re-enabled, and the
   * server's reason was painted on a surface the modal hid. Rendered inside DialogContent instead.
   */
  const [pendingFailure, setPendingFailure] = useState<{
    message: string;
    httpStatus: number | null;
  } | null>(null);

  /** Opening or dismissing the confirm dialog always starts from a clean failure slate. */
  const closePending = () => {
    setPending(null);
    setPendingFailure(null);
  };
  const openPending = (action: PendingAction) => {
    setPendingFailure(null);
    setPending(action);
  };

  const describeFailure = (result: { kind: string; status?: number | null; message?: string }) => {
    if (result.kind === 'forbidden') {
      return {
        message: 'Refused — authoring a plan master needs provider-admin permission.',
        httpStatus: 403,
      };
    }
    if (result.kind === 'unauthorized') {
      return { message: 'Your session is not valid. Sign in again.', httpStatus: 401 };
    }
    if (result.kind === 'notFound') {
      return { message: result.message ?? 'Not found', httpStatus: 404 };
    }
    return { message: result.message ?? 'The request failed', httpStatus: result.status ?? null };
  };

  const runPending = async () => {
    if (!pending) return;
    setBusy(true);
    setPendingFailure(null);
    const { row } = pending;

    // Delete returns ApiResult<void> (no version payload), so handle it on its own path rather than
    // folding it into the publish/newVersion result that both carry a { version } body.
    if (pending.kind === 'delete') {
      const del = await deleteMaster(row.planMasterUid, row.version, accessToken);
      setBusy(false);
      if (del.kind !== 'ok') {
        setPendingFailure(describeFailure(del));
        if (del.kind === 'rejected') reload();
        return;
      }
      closePending();
      setNotice(`Deleted the tenant-contributed draft “${row.name ?? 'plan'}” v${row.version}.`);
      reload();
      return;
    }

    const result =
      pending.kind === 'publish'
        ? await publishVersion(row.planMasterUid, row.version, accessToken)
        : await openNewVersion(row.planMasterUid, accessToken);
    setBusy(false);
    if (result.kind !== 'ok') {
      setPendingFailure(describeFailure(result));
      // A 400/409 means the server's view of this version's status is not the grid's (typically:
      // the version is no longer a DRAFT). Re-read so the row settles on the real status instead of
      // continuing to offer an action the server has already refused.
      if (result.kind === 'rejected') reload();
      return;
    }
    closePending();
    setNotice(
      pending.kind === 'publish'
        ? `Published v${result.data.version}. Any previously published version is now superseded — tenants forking from now on get this one.`
        : `Opened v${result.data.version} as a draft, with the published version's steps deep-copied onto it.`,
    );
    reload();
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'name', headerName: 'Plan', flex: 1.6, minWidth: 200 },
      { field: 'version', headerName: 'Version', width: 100, type: 'number' },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: (params: GridRenderCellParams<PlanRow>) => (
          <Chip
            size="small"
            label={statusLabel(params.row.status)}
            color={statusColor(params.row.status)}
          />
        ),
      },
      { field: 'description', headerName: 'Description', flex: 1.4, minWidth: 180 },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        // Preview icon + Edit steps / Publish / New version; widened so the last never clips.
        width: 500,
        renderCell: (params: GridRenderCellParams<PlanRow>) => {
          const row = params.row;
          const draft = isPublishable(row.status);
          return (
            <Stack direction="row" spacing={0.75} alignItems="center">
              {/* Preview (read-only) — the only way to see a PUBLISHED plan's steps, since its
                  "Edit steps" is disabled. Available on every row, draft or published. */}
              <Tooltip title="Preview steps">
                <IconButton
                  size="small"
                  aria-label="Preview steps"
                  onClick={() => setPreviewTarget(row)}
                  data-testid={`preview-${row.id}`}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Version history (gap-fix #2) — the plan family's whole lineage, grouped. */}
              <Tooltip title="Version history">
                <IconButton
                  size="small"
                  aria-label="Version history"
                  onClick={() => setHistoryTarget(row)}
                  data-testid={`history-${row.id}`}
                >
                  <HistoryOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Edit steps — DRAFT-only (replaceSteps 400s otherwise). HIDDEN for a tenant-
                  contributed plan: a plan a tenant shared is not edited by the provider — it is
                  Publish-or-Delete only. Origin = `contributedByTenant` (null = provider-authored). */}
              {!row.contributedByTenant && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!draft}
                  onClick={() =>
                    setBuilderTarget({
                      planMasterUid: row.planMasterUid,
                      version: row.version,
                      status: row.status,
                      name: row.name ?? '',
                      description: row.description,
                      // Pre-fill plan-level values so editing does not blank lead source / trigger /
                      // next plan (the builder shows and re-saves the row's current values).
                      leadSource: row.leadSource,
                      entryStage: row.entryStage,
                      nextPlanMasterUid: row.nextPlanMasterUid,
                    })
                  }
                  data-testid={`edit-${row.id}`}
                >
                  Edit steps
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                disabled={!draft}
                onClick={() => openPending({ kind: 'publish', row })}
                data-testid={`publish-${row.id}`}
              >
                Publish
              </Button>
              {/* New version is a provider-authoring action — also hidden for tenant-contributed. */}
              {!row.contributedByTenant && (
                <Button
                  size="small"
                  variant="text"
                  disabled={!canOpenNewVersion(row.status)}
                  onClick={() => openPending({ kind: 'newVersion', row })}
                  data-testid={`newversion-${row.id}`}
                >
                  New version
                </Button>
              )}
              {/* Delete — offered ONLY for a tenant-contributed plan (Publish-or-Delete model). A
                  provider-authored master is still managed via Publish / New version only. */}
              {row.contributedByTenant && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  disabled={!draft}
                  onClick={() => openPending({ kind: 'delete', row })}
                  data-testid={`delete-${row.id}`}
                >
                  Delete
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }} data-testid="automation-plans-page">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Automation plan masters</Typography>
          <Typography variant="body2" color="text.secondary">
            Reusable base templates that tenants fork.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Positioned like the Message Templates search — inline, at the far right of the header
              row — rather than on its own line below, for a consistent search placement across the
              provider-web catalog screens. */}
          <TextField
            size="small"
            placeholder="Search plans by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search plan masters"
            inputProps={{ 'data-testid': 'plans-search' }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
                transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.light' },
                '&.Mui-focused': { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}1f` },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" size="small" onClick={reload}>
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              // ONE builder for the whole flow: opening it in create mode authors name, plan-level
              // attributes and steps together, and the master is minted only on Save & close. The
              // old name-only "New plan master" dialog (two screens to make one plan) is gone.
              setBuilderTarget({
                planMasterUid: '',
                version: 1,
                status: 'DRAFT',
                name: '',
                description: '',
                createMode: true,
              })
            }
            data-testid="new-plan-button"
          >
            New plan
          </Button>
        </Stack>
      </Stack>

      {notice && (
        <Alert severity={noticeSeverity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {/* Every failure this screen can produce now renders inside the dialog that caused it — a
          page-level banner here would sit behind an open modal, which is the defect this replaced. */}

      <AsyncBoundary
        state={state}
        what="The automation plan master catalog"
        onRetry={reload}
        loadingLabel="Loading plan masters…"
        isEmpty={(rows) => toRows(rows).length === 0}
        emptyTitle="No plan masters yet"
        emptyDetail='No plan masters have been created yet. Use “New plan” to create the first draft.'
      >
        {(rows) => {
          const filteredRows = toRows(rows).filter((row) => matchesSearch(row, search));
          if (search.trim() && filteredRows.length === 0) {
            return (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No plan masters match “{search.trim()}”.
                </Typography>
              </Paper>
            );
          }
          return (
            <Paper
              variant="outlined"
              // Fill the viewport height instead of a hard-coded 560 (which left a large dead gap
              // below the grid on tall screens). GRID_TOP_OFFSET_PX covers the fixed topbar + this
              // page's header/padding; minHeight keeps it usable on short screens.
              sx={{ height: `calc(100vh - ${GRID_TOP_OFFSET_PX}px)`, minHeight: 420, width: '100%' }}
            >
              <DataGrid
                rows={filteredRows}
                columns={columns}
                disableRowSelectionOnClick
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[10, 25, 50]}
                data-testid="plans-grid"
              />
            </Paper>
          );
        }}
      </AsyncBoundary>

      <Dialog open={pending != null} onClose={closePending} fullWidth maxWidth="sm">
        <DialogTitle>
          {pending?.kind === 'publish'
            ? `Publish “${pending.row.name ?? 'plan'}” v${pending.row.version}?`
            : pending?.kind === 'delete'
            ? `Delete “${pending.row.name ?? 'plan'}” v${pending.row.version}?`
            : `Open a new version of “${pending?.row.name ?? 'plan'}”?`}
        </DialogTitle>
        <DialogContent>
          {/* Each blast radius spelled out, because neither is guessable from its verb. */}
          {pending?.kind === 'publish' && (
            <DialogContentText>
              This version becomes the one every tenant forks from. Any currently published version
              is demoted to <strong>superseded</strong> — only one version can be published at a
              time. Tenant forks already running are unaffected.
            </DialogContentText>
          )}
          {pending?.kind === 'newVersion' && (
            <DialogContentText>
              Opens the next version as a <strong>draft</strong>, deep-copying the steps of the
              currently published version. The published version stays published until you publish
              the new one.
            </DialogContentText>
          )}
          {pending?.kind === 'delete' && (
            <DialogContentText>
              Removes this tenant-contributed draft from the catalog. It is a soft delete of this
              version. Use this when a shared plan should not be published — the provider does not
              edit tenant-contributed plans, only <strong>Publish</strong> or <strong>Delete</strong>.
            </DialogContentText>
          )}
          {/* Inside the dialog, not on the page behind it — see `pendingFailure`. */}
          {pendingFailure && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="pending-action-error">
              {pendingFailure.message}
              {pendingFailure.httpStatus != null && ` (HTTP ${pendingFailure.httpStatus})`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePending}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={runPending}
            data-testid="confirm-action"
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read-only step preview — opened from the eye icon on any row. */}
      {previewTarget && (
        <PlanStepsPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
      )}

      {/* Read-only version-history dialog (gap-fix #2) — opened from the row's history icon. */}
      {historyTarget && (
        <PlanVersionHistoryDialog target={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}

      {/* The plan-master builder — the tenant-parity modal, opened from "New plan" / "Edit steps". */}
      {builderTarget && (
        <PlanMasterBuilder
          target={builderTarget}
          onClose={() => setBuilderTarget(null)}
          onSaved={(message, severity = 'success') => {
            setNotice(message);
            setNoticeSeverity(severity);
            reload();
          }}
        />
      )}
    </Box>
  );
};

export default AutomationPlansPage;
